// Health Survey Validation Utilities
import { message } from 'antd';

/**
 * Validates general health assessment form fields
 * @param {Object} form - Ant Design form instance
 * @param {Function} t - Translation function
 * @returns {Promise<Object>} - Validation result with values or error info
 */
export const validateGeneralHealthAssessment = async (form, t) => {
  try {
    const values = await form.validateFields();
    return { success: true, values };
  } catch (errorInfo) {
    const missingFields = errorInfo.errorFields || [];
    
    if (missingFields.length > 0) {
      // Focus on the first missing field
      const firstMissingField = missingFields[0];
      const fieldName = Array.isArray(firstMissingField.name) 
        ? firstMissingField.name[0] 
        : firstMissingField.name;
      
      // Scroll to the first missing field with improved targeting
      setTimeout(() => {
        scrollToMissingField(fieldName, 'general-health');
      }, 100);
      
      message.error(t('healthSurvey.validation.missingRequiredFields'));
    }
    
    return { 
      success: false, 
      missingFields,
      firstMissingField: missingFields[0]?.name
    };
  }
};

/**
 * Validates lifestyle questionnaire forms
 * @param {Object} form - Ant Design form instance
 * @param {Array} categories - Lifestyle categories
 * @param {Function} t - Translation function
 * @returns {Promise<Object>} - Validation result with values or error info
 */
export const validateLifestyleQuestionnaires = async (form, categories, t) => {
  try {
    // Get all form values
    const allValues = form.getFieldsValue();
    const missingFields = [];
    
    // Check each category for required fields
    categories.forEach(category => {
      const categoryValues = allValues[category.key] || {};
      
      category.parameters.forEach(param => {
        // Only validate Questionnaire type parameters (required fields)
        if (param.type === 'Questionnaire') {
          const value = categoryValues[param.key];
          if (value === undefined || value === null || value === '') {
            missingFields.push({
              category: category.key,
              categoryTitle: category.title,
              field: param.key,
              fieldName: param.questionText || param.name,
              name: [category.key, param.key]
            });
          }
        }
      });
    });
    
    if (missingFields.length > 0) {
      // Navigate to first missing field with improved accordion handling
      const firstMissing = missingFields[0];

      setTimeout(() => {
        scrollToLifestyleMissingField(firstMissing);
      }, 100);
      
      message.error(t('healthSurvey.validation.missingRequiredFields'));
      
      return {
        success: false,
        missingFields,
        firstMissingField: firstMissing
      };
    }
    
    return { success: true, values: allValues };
  } catch (error) {
    console.error('Lifestyle validation error:', error);
    message.error(t('healthSurvey.validationFailed'));
    return { success: false, error };
  }
};

/**
 * Calculates completion status for progress indicator
 * @param {Object} values - Form values
 * @param {Array} requiredFields - Array of required field names
 * @returns {Object} - Completion status
 */
export const calculateCompletionStatus = (values, requiredFields) => {
  const completed = requiredFields.filter(field => {
    if (Array.isArray(field)) {
      // Handle nested fields like ['category', 'field']
      const [category, fieldName] = field;
      return values[category] && values[category][fieldName] !== undefined && values[category][fieldName] !== null && values[category][fieldName] !== '';
    } else {
      // Handle top-level fields
      return values[field] !== undefined && values[field] !== null && values[field] !== '';
    }
  }).length;
  
  return {
    completed,
    total: requiredFields.length,
    percentage: Math.round((completed / requiredFields.length) * 100)
  };
};

/**
 * Gets all required fields for general health assessment
 * @returns {Array} - Array of required field names
 */
export const getGeneralHealthRequiredFields = () => {
  return ['newDiagnosis', 'hospitalization', 'healthImprovement'];
};

/**
 * Gets all required fields for lifestyle questionnaires
 * @param {Array} categories - Lifestyle categories
 * @returns {Array} - Array of required field names (nested format)
 */
export const getLifestyleRequiredFields = (categories) => {
  const requiredFields = [];
  
  categories.forEach(category => {
    category.parameters.forEach(param => {
      if (param.type === 'Questionnaire') {
        requiredFields.push([category.key, param.key]);
      }
    });
  });
  
  return requiredFields;
};

/**
 * Scrolls to a missing field in general health assessment
 * @param {string} fieldName - Name of the missing field
 * @param {string} context - Context for better targeting
 */
export const scrollToMissingField = (fieldName, context = '') => {
  // Multiple selectors to find the field element
  const simplifiedName = fieldName.replace(/\[|\]/g, '').replace(/\./g, '-');
  const selectors = [
    `[data-field="${fieldName}"]`,
    `[data-field-simple="${simplifiedName}"]`,
    `[name="${fieldName}"]`,
    `input[id*="${fieldName}"]`,
    `.ant-form-item:has([name="${fieldName}"])`,
    `.ant-form-item:has(input[id*="${fieldName}"])`,
    `.ant-radio-group[id*="${fieldName}"]`,
    `label[for*="${fieldName}"]`,
    // Additional selectors for common field patterns
    `.ant-form-item-label:contains("${fieldName}")`,
    `[data-field*="${fieldName}"]`
  ];

  let fieldElement = null;

  // Try each selector until we find the element
  for (const selector of selectors) {
    try {
      fieldElement = document.querySelector(selector);
      if (fieldElement) break;
    } catch (e) {
      // Skip invalid selectors
      continue;
    }
  }

  // If we still haven't found it, try a broader search
  if (!fieldElement) {
    // Look for form items containing the field name in text content
    const formItems = document.querySelectorAll('.ant-form-item');
    for (const item of formItems) {
      const label = item.querySelector('.ant-form-item-label');
      if (label && label.textContent.toLowerCase().includes(fieldName.toLowerCase())) {
        fieldElement = item;
        break;
      }
    }
  }

  if (fieldElement) {
    // Scroll to the element
    fieldElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    });

    // Add visual highlight
    fieldElement.style.transition = 'all 0.3s ease';
    fieldElement.style.backgroundColor = '#fff2f0';
    fieldElement.style.border = '2px solid #ff4d4f';
    fieldElement.style.borderRadius = '6px';
    fieldElement.style.padding = '8px';

    // Try to focus on the input element
    setTimeout(() => {
      const focusableElement = fieldElement.querySelector('input, .ant-radio-group, .ant-checkbox-group, button') || fieldElement;
      if (focusableElement && focusableElement.focus) {
        focusableElement.focus();
      }

      // Remove highlight after a few seconds
      setTimeout(() => {
        fieldElement.style.backgroundColor = '';
        fieldElement.style.border = '';
        fieldElement.style.padding = '';
      }, 3000);
    }, 500);

    console.log(`Scrolled to missing field: ${fieldName}`);
  } else {
    console.warn(`Could not find field element for: ${fieldName}`);
  }
};

/**
 * Scrolls to a missing field in lifestyle questionnaires with accordion handling
 * @param {Object} missingField - Missing field information
 */
export const scrollToLifestyleMissingField = (missingField) => {
  const { category, categoryTitle, field, fieldName } = missingField;

  // First, try to expand the accordion panel
  const expandAccordion = () => {
    // Look for accordion panel header
    const panelSelectors = [
      `[data-category="${category}"]`,
      `.ant-collapse-header:contains("${categoryTitle}")`,
      `.ant-collapse-item .ant-collapse-header`
    ];

    let panelHeader = null;
    for (const selector of panelSelectors) {
      try {
        if (selector.includes(':contains')) {
          // Handle text-based selector manually
          const headers = document.querySelectorAll('.ant-collapse-header');
          for (const header of headers) {
            if (header.textContent.includes(categoryTitle)) {
              panelHeader = header;
              break;
            }
          }
        } else {
          panelHeader = document.querySelector(selector);
        }
        if (panelHeader) break;
      } catch (e) {
        continue;
      }
    }

    // If we found a panel header, check if it needs to be expanded
    if (panelHeader) {
      const panelItem = panelHeader.closest('.ant-collapse-item');
      if (panelItem && !panelItem.classList.contains('ant-collapse-item-active')) {
        panelHeader.click();
        console.log(`Expanded accordion panel for category: ${categoryTitle}`);
      }
    }
  };

  // Expand accordion first
  expandAccordion();

  // Wait for accordion animation, then scroll to field
  setTimeout(() => {
    const fieldKey = `${category}.${field}`;
    const simplifiedKey = fieldKey.replace(/\[|\]/g, '').replace(/\./g, '-');
    const fieldSelectors = [
      `[data-field="${fieldKey}"]`,
      `[data-field-simple="${simplifiedKey}"]`,
      `[data-field*="${field}"]`,
      `[name*="${field}"]`,
      `input[id*="${field}"]`,
      `.ant-form-item:has([name*="${field}"])`,
      `.ant-radio-group[id*="${field}"]`,
      // Handle nested form names like ['category', 'field']
      `[name="${category}[${field}]"]`,
      `input[name="${category}[${field}]"]`
    ];

    let fieldElement = null;

    // Try each selector
    for (const selector of fieldSelectors) {
      try {
        fieldElement = document.querySelector(selector);
        if (fieldElement) break;
      } catch (e) {
        continue;
      }
    }

    // Broader search if needed
    if (!fieldElement) {
      const formItems = document.querySelectorAll('.ant-form-item');
      for (const item of formItems) {
        const label = item.querySelector('.ant-form-item-label');
        if (label && (
          label.textContent.includes(fieldName) ||
          label.textContent.includes(field)
        )) {
          fieldElement = item;
          break;
        }
      }
    }

    if (fieldElement) {
      // Scroll to the field
      fieldElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });

      // Add visual highlight
      fieldElement.style.transition = 'all 0.3s ease';
      fieldElement.style.backgroundColor = '#fff2f0';
      fieldElement.style.border = '2px solid #ff4d4f';
      fieldElement.style.borderRadius = '6px';
      fieldElement.style.padding = '8px';

      // Focus on the input
      setTimeout(() => {
        const focusableElement = fieldElement.querySelector('input, .ant-radio-group, .ant-checkbox-group, button') || fieldElement;
        if (focusableElement && focusableElement.focus) {
          focusableElement.focus();
        }

        // Remove highlight after a few seconds
        setTimeout(() => {
          fieldElement.style.backgroundColor = '';
          fieldElement.style.border = '';
          fieldElement.style.padding = '';
        }, 3000);
      }, 500);

      console.log(`Scrolled to missing lifestyle field: ${categoryTitle} - ${fieldName}`);
    } else {
      console.warn(`Could not find lifestyle field element for: ${categoryTitle} - ${fieldName}`);
    }
  }, 400); // Wait for accordion animation
};

/**
 * Adds visual indicators to form fields
 * @param {string} containerId - Container element ID
 */
export const addVisualIndicators = (containerId) => {
  setTimeout(() => {
    const container = document.getElementById(containerId) || document.body;

    // Add data attributes to form items for easier targeting
    const formItems = container.querySelectorAll('.ant-form-item');
    formItems.forEach(item => {
      // Try multiple ways to get the field name
      const input = item.querySelector('input, .ant-radio-group, .ant-checkbox-group, textarea, select');
      const label = item.querySelector('.ant-form-item-label label');

      let fieldName = null;

      if (input) {
        // Get name from input element
        fieldName = input.getAttribute('name') || input.getAttribute('id');

        // Handle nested names like ['category', 'field']
        if (!fieldName && input.name) {
          fieldName = input.name;
        }

        // For radio groups, try to get the name from radio buttons
        if (!fieldName && input.classList.contains('ant-radio-group')) {
          const radioButton = input.querySelector('input[type="radio"]');
          if (radioButton) {
            fieldName = radioButton.getAttribute('name');
          }
        }
      }

      // If we still don't have a field name, try to extract from label
      if (!fieldName && label) {
        const forAttr = label.getAttribute('for');
        if (forAttr) {
          fieldName = forAttr;
        }
      }

      // Set data attribute if we found a field name
      if (fieldName) {
        item.setAttribute('data-field', fieldName);

        // Also add a simplified version for easier targeting
        const simplifiedName = fieldName.replace(/\[|\]/g, '').replace(/\./g, '-');
        item.setAttribute('data-field-simple', simplifiedName);
      }
    });

    // Add styling for required fields
    const requiredItems = container.querySelectorAll('.ant-form-item-required');
    requiredItems.forEach(item => {
      item.style.borderLeft = '3px solid #ff4d4f';
      item.style.paddingLeft = '12px';
      item.style.marginLeft = '-15px';
    });
  }, 100);
};
